import { useMemo, useState, type JSX } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { FormattedMessage } from 'react-intl';
import Carousel from '@alltrails/denali/components/Carousel';
import Link from '@alltrails/denali/components/Link';
import Gallery from '@alltrails/denali/icons/Gallery';
import { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import ReviewPhoto from '@alltrails/shared/types/ReviewPhoto';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import type Context from '@alltrails/core/types/Context';
import type Review from '@alltrails/core/types/Review';
import styles from './styles/styles.module.scss';
import TrailReviewPhoto from './TrailReviewPhoto';

const Lightbox = dynamic(() => import('@alltrails/lightbox').then(mod => mod.Lightbox), { ssr: false });

type CarouselItem = (ReviewPhoto & { type: 'photo' }) | { type: 'showAllLink'; id: string; isShowAllLink: true };

type TrailReviewPhotosProps = {
  className?: string;
  languageRegionCode: LanguageRegionCode;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  review: Review & { hasMorePhotos?: boolean };
  context?: Context;
  linkToActivity?: string;
  maxSlidesPerView?: number;
};

const TrailReviewPhotos = ({
  className,
  languageRegionCode,
  onLightboxClose,
  onLightboxPhotoViewed,
  onLightboxUserClick,
  onReviewPhotosScroll,
  onReviewPhotosScrollEnd,
  onReviewPhotoClick,
  review,
  context,
  linkToActivity,
  maxSlidesPerView = 6.5
}: TrailReviewPhotosProps): JSX.Element => {
  const { photos, hasMorePhotos } = review;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [startingIdx, setStartingIdx] = useState(0);
  const lightboxImages = useMemo(
    () =>
      photos!.map(photo => ({
        id: photo.id,
        url: getPhotoUrl({ isOptimized: true, photo, size: '2048x2048' })
      })),
    [photos]
  );

  const onPhotoFocus = (ev: React.FocusEvent<HTMLButtonElement>) => {
    ev.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  };

  const onPhotoClick = (photo: ReviewPhoto, idx: number, ev: React.MouseEvent<HTMLButtonElement>) => {
    onReviewPhotoClick?.(review, photo);
    setStartingIdx(idx);
    setLightboxOpen(true);
    ev.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  };

  const onPhotoViewed = (photo: LightboxPhoto['photo']) => {
    onLightboxPhotoViewed?.(review, photo);
  };

  const onLightboxCloseInternal = () => {
    onLightboxClose?.();
    setLightboxOpen(false);
  };

  const truncatedPhotos = photos!.slice(0, 10);

  const carouselItems = useMemo((): CarouselItem[] => {
    const items: CarouselItem[] = truncatedPhotos.map(photo => ({ ...photo, type: 'photo' as const }));
    if (hasMorePhotos && linkToActivity) {
      items.push({ type: 'showAllLink', id: 'show-all-photos', isShowAllLink: true });
    }
    // invisible spacer to ensure last image is fully visible
    items.push({ type: 'spacer', id: 'spacer', isSpacer: true } as any);
    return items;
  }, [truncatedPhotos, hasMorePhotos, linkToActivity]);

  const renderCarouselItem = (item: CarouselItem, index: number) => {
    if (item.type === 'showAllLink') {
      return (
        <Link className={styles.showAllPhotos} href={linkToActivity!} noUnderline>
          <Gallery />
          <FormattedMessage defaultMessage="Show all" />
        </Link>
      );
    }

    if ((item as any).isSpacer) {
      return <div className={styles.spacer} />;
    }

    return (
      <TrailReviewPhoto key={item.id} idx={index} onPhotoClick={onPhotoClick} onPhotoFocus={onPhotoFocus} photo={item} photoCount={photos!.length} />
    );
  };

  const getItemKey = (item: CarouselItem, _index: number): string => {
    return item.type === 'showAllLink' ? item.id : String(item.id);
  };

  return (
    <div className={styles.photosContainer} data-testid="trail-review-photos">
      <Carousel
        uniqueId="trail-review-photos-carousel"
        items={carouselItems}
        renderItem={renderCarouselItem}
        getItemKey={getItemKey}
        className={classNames(styles.photoCarousel, className)}
        slidesPerViewMobile={2.5}
        slidesPerViewBreakpoints={{ 480: 3, 760: 4, 1120: maxSlidesPerView }}
        showPaginationDots={false}
        showOverflow={false}
        preventOverflowOnLargeScreens={false}
        scrollMode={false}
        staticArrows={false}
        onSlideChange={index => {
          const numPhotos = photos!.length;
          onReviewPhotosScroll?.(review, numPhotos);
          if (index >= carouselItems.length - 1) {
            onReviewPhotosScrollEnd?.(review, numPhotos);
          }
        }}
      />
      {lightboxOpen && (
        <Lightbox
          context={context}
          languageRegionCode={languageRegionCode}
          closeModal={onLightboxCloseInternal}
          onPhotoViewed={onPhotoViewed}
          onUserClick={onLightboxUserClick}
          photos={lightboxImages}
          startingIndex={startingIdx}
          hidePageScrollbarGutter
        />
      )}
    </div>
  );
};

export default TrailReviewPhotos;
